/**
 * Shared "safe to render inline" logic for file previews (the Files tab's
 * AttachmentPanel and the print-window attachment review). `content_type` is
 * guessed from the uploaded FILENAME (mimetypes.guess_type on the backend),
 * never sniffed from the bytes — a file named "x.html" or "x.svg" gets that
 * content_type regardless of what it contains. Rendering it inline or
 * navigating a same-origin blob: tab to it would let embedded script run in
 * this app's origin. So the inline-render allowlist is a closed set of types
 * that browsers never treat as executable, not a prefix match. Images and
 * text/plain are inert no matter what the bytes actually are (an <img> never
 * executes embedded script; text/plain is rendered as escaped React text,
 * not HTML/an iframe). application/pdf is the one type still handed to a
 * native browser renderer, so its bytes are checked against the real PDF
 * magic number before ever reaching <object> or a same-origin blob: tab —
 * content_type can only be "application/pdf" for a file literally named
 * *.pdf, but nothing stops that file's actual bytes from being something
 * else entirely.
 */
export const SAFE_PREVIEW_IMAGE_TYPE_LIST = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/bmp', 'image/x-icon',
]
export const SAFE_PREVIEW_IMAGE_TYPES = new Set(SAFE_PREVIEW_IMAGE_TYPE_LIST)
export const SAFE_PREVIEW_TYPES = new Set(
  SAFE_PREVIEW_IMAGE_TYPE_LIST.concat(['application/pdf', 'text/plain']),
)

export function canPreviewInline(contentType: string): boolean {
  return SAFE_PREVIEW_TYPES.has(contentType)
}

export const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d] // ASCII "%PDF-"

export async function blobStartsWith(blob: Blob, magic: number[]): Promise<boolean> {
  const head = new Uint8Array(await blob.slice(0, magic.length).arrayBuffer())
  return head.length === magic.length && magic.every((b, i) => head[i] === b)
}
